import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageLayout from "../../components/layout/PageLayout";
import LicitacionMenu from "./LicitacionMenu";
import { ciudadesPeru } from "../../Data/PeruDistricts";
import filtrosTender from "../../Data/FiltrosTender";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const quickDateOptions = [
  { key: "1semana", label: "1 Semana", months: 0, days: 7 },
  { key: "1mes", label: "1 Mes", months: 1 },
  { key: "3meses", label: "3 Meses", months: 3 },
  { key: "6meses", label: "6 Meses", months: 6 },
];

const chipClass = (selected) =>
  cn(
    "cursor-pointer rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted",
    selected && "border-primary bg-primary/10 text-primary"
  );

const CreateTenderStep3 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialData = location.state?.formData || {};
  const [showAllCities, setShowAllCities] = useState(false);

  const calcularSeleccionRapida = (fechaISO) => {
    const hoy = new Date();
    const fecha = new Date(fechaISO);
    const diferenciaDias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));

    if (diferenciaDias === 7) return "1semana";
    if (diferenciaDias >= 28 && diferenciaDias <= 31) return "1mes";
    if (diferenciaDias >= 89 && diferenciaDias <= 92) return "3meses";
    if (diferenciaDias >= 179 && diferenciaDias <= 184) return "6meses";

    return "";
  };

  // Nueva función para calcular la fecha de 3 meses por defecto
  const getFechaDefault = () => {
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() + 3);
    return fecha.toISOString().split("T")[0];
  };

  // Luego actualiza tu useState así:
  const [formData, setFormData] = useState({
    ciudad: initialData.ciudad || [],
    condiciones_pago: initialData.condiciones_pago || [],
    duracion_contrato: initialData.duracion_contrato || [],
    fecha_maxima_postulacion:
      initialData.fecha_maxima_postulacion || getFechaDefault(), // ✅ Fecha por defecto: 3 meses
    seleccion_rapida:
      initialData.seleccion_rapida ||
      calcularSeleccionRapida(
        initialData.fecha_maxima_postulacion || getFechaDefault()
      ), // ✅ Mantiene coherencia
    ...initialData,
  });

  const handleInputChange = (name, value) => {
    if (Array.isArray(formData[name])) {
      const updatedValues = formData[name].includes(value)
        ? formData[name].filter((item) => item !== value) // Elimina si ya está seleccionado
        : [...formData[name], value]; // Agrega si no está seleccionado

      setFormData({
        ...formData,
        [name]: updatedValues,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleQuickDate = (option) => {
    const fechaCalculada = new Date();
    if (option.days) {
      fechaCalculada.setDate(fechaCalculada.getDate() + option.days);
    } else {
      fechaCalculada.setMonth(fechaCalculada.getMonth() + option.months);
    }
    setFormData({
      ...formData,
      seleccion_rapida: option.key,
      fecha_maxima_postulacion: fechaCalculada.toISOString().split("T")[0],
    });
  };

  const handleNext = (e) => {
    e.preventDefault();

    const missingFields = [];
    if (!formData.fecha_maxima_postulacion)
      missingFields.push("Definir la duración de la convocatoria.");
    if (!formData.ciudad.length)
      missingFields.push("Seleccionar al menos una ciudad.");
    if (!formData.condiciones_pago.length)
      missingFields.push("Seleccionar al menos una condición de pago.");
    if (!formData.duracion_contrato.length)
      missingFields.push("Seleccionar al menos una duración de contrato.");

    if (missingFields.length > 0) {
      toast.warning(
        `Por favor completa lo siguiente:\n- ${missingFields.join("\n- ")}`
      );
      return;
    }

    navigate("/create-tender-step-4", {
      state: { formData },
    });
  };

  const handleBack = () => {
    navigate("/create-tender-step-2", { state: { formData } });
  };

  return (
    <PageLayout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <LicitacionMenu currentStep={3} />

        <h3 className="text-xl font-semibold text-foreground">Información Adicional para Proveedores</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Responde las preguntas para darle información adicional a los proveedores.
        </p>
        <form className="mt-6 flex flex-col gap-8">
          {/* Selección de fecha final */}
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              1. Define la duración de la convocatoria (a partir de hoy):
            </label>

            <div className="flex flex-wrap gap-2">
              {quickDateOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={chipClass(formData.seleccion_rapida === option.key)}
                  onClick={() => handleQuickDate(option)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="mt-3 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
              <span className="text-sm text-muted-foreground">O escoge una fecha de cierre:</span>
              <Input
                type="date"
                className="sm:w-auto"
                value={formData.fecha_maxima_postulacion}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    fecha_maxima_postulacion: e.target.value,
                    seleccion_rapida: "", // Limpiar opción rápida
                  })
                }
              />
            </div>
          </div>

          {/* Selección de Ciudades */}
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              2. ¿En qué ciudades se realizará el servicio? Marca todas las alternativas de ciudades que
              involucren el servicio.
            </label>

            <div className="flex flex-wrap gap-2">
              {(showAllCities ? ciudadesPeru : ciudadesPeru.slice(0, 15)).map((ciudad) => (
                <div
                  key={ciudad}
                  onClick={() => handleInputChange("ciudad", ciudad)}
                  className={chipClass(formData.ciudad.includes(ciudad))}
                >
                  {ciudad}
                </div>
              ))}
              {!showAllCities && ciudadesPeru.length > 15 && (
                <button
                  type="button"
                  onClick={() => setShowAllCities(true)}
                  className="rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
                >
                  Ver más
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  setFormData({
                    ...formData,
                    ciudad: formData.ciudad.length === ciudadesPeru.length ? [] : ciudadesPeru,
                  })
                }
                className="rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
              >
                {formData.ciudad.length === ciudadesPeru.length ? "Quitar Todas" : "Seleccionar Todas"}
              </button>
            </div>
          </div>

          {/* Condiciones de Pago */}
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              3. ¿Qué condiciones de pago ofreces a tus proveedores?
            </label>

            <div className="flex flex-wrap gap-2">
              {filtrosTender.condicionesPago.map(({ value, label }) => (
                <div
                  key={value}
                  onClick={() => handleInputChange("condiciones_pago", value)}
                  className={chipClass(formData.condiciones_pago.includes(value))}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Duración del Contrato */}
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              4. ¿Qué duración de contrato ofreces?
            </label>

            <div className="flex flex-wrap gap-2">
              {filtrosTender.duracionContrato.map(({ value, label }) => (
                <div
                  key={value}
                  onClick={() => handleInputChange("duracion_contrato", value)}
                  className={chipClass(formData.duracion_contrato.includes(value))}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Retroceder
            </Button>
            <Button type="button" onClick={handleNext}>
              Siguiente
            </Button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

export default CreateTenderStep3;
